import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getChecklistTemplateById, createChecklistInstance } from './checklistApi';
import { Document, Page, Text, View, StyleSheet, Font, pdf, Image } from '@react-pdf/renderer';
import CommonLayout from '../../components/CommonLayout';
import PageTitle from '../../components/PageTitle';
import { Skeleton } from '../../components/Loaders/Skeleton/Skeleton';
import { ChevronDown, ChevronUp, Copy } from 'lucide-react';
import { cancelButton, pageActionsContainer, confirmButton } from '../../components/Buttons';
import appLogo from '../../assets/logo/IconOnly_Transparent_NoBuffer.png';
import ConfirmationDialog from '../../components/dialogs/ConfirmationDialog';
import './ChecklistInstance.scss';

// Register Hebrew font
Font.register({
    family: 'Noto',
    fonts: [
        {
            src: '/fonts/noto/static/NotoSansHebrew-Regular.ttf',
            fontWeight: 400,
        },
        {
            src: '/fonts/noto/static/NotoSansHebrew-Bold.ttf',
            fontWeight: 700,
        },
    ]
});

// Text rendering helper (same as ChecklistPDF)
const renderTextWithDirections = (text) => {
    if (!text) return <Text></Text>;

    const segments = text.split(/(\s+)|([0-9]+.[0-9]+)|([,.\-_:;()'"]+)|([a-zA-Z0-9][/a-zA-Z0-9\s]*)|([־\u0590-\u05FF]+)/g)
        .filter(segment => segment !== '' && segment !== undefined);

    const combinedSegments = [];
    let currentEnglishSegment = '';

    segments.forEach((segment) => {
        if (/^\s+$/.test(segment)) {
            if (currentEnglishSegment) {
                combinedSegments.push({
                    text: currentEnglishSegment.trim(),
                    isEnglish: true,
                    isSpace: false,
                    isPunctuation: false
                });
                currentEnglishSegment = '';
            }
            combinedSegments.push({
                text: ' ',
                isEnglish: false,
                isSpace: true,
                isPunctuation: false
            });
            return;
        }

        if (/^[,.\-_:;()'"]+$/.test(segment)) {
            if (currentEnglishSegment) {
                combinedSegments.push({
                    text: currentEnglishSegment.trim(),
                    isEnglish: true,
                    isSpace: false,
                    isPunctuation: false
                });
                currentEnglishSegment = '';
            }
            combinedSegments.push({
                text: segment,
                isEnglish: false,
                isSpace: false,
                isPunctuation: true
            });
            return;
        }

        const isEnglish = /^[a-zA-Z0-9][a-zA-Z0-9\s]*$/.test(segment);

        if (isEnglish) {
            currentEnglishSegment = currentEnglishSegment
                ? `${currentEnglishSegment}${segment}`
                : segment;
        } else {
            if (currentEnglishSegment) {
                combinedSegments.push({
                    text: currentEnglishSegment.trim(),
                    isEnglish: true,
                    isSpace: false,
                    isPunctuation: false
                });
                currentEnglishSegment = '';

                if (!combinedSegments[combinedSegments.length - 1]?.isSpace) {
                    combinedSegments.push({
                        text: ' ',
                        isEnglish: false,
                        isSpace: true,
                        isPunctuation: false
                    });
                }
            }

            combinedSegments.push({
                text: segment,
                isEnglish: false,
                isSpace: false,
                isPunctuation: false
            });
        }
    });

    if (currentEnglishSegment) {
        if (combinedSegments.length > 0 &&
            !combinedSegments[combinedSegments.length - 1].isEnglish &&
            !combinedSegments[combinedSegments.length - 1].isSpace) {
            combinedSegments.push({
                text: ' ',
                isEnglish: false,
                isSpace: true,
                isPunctuation: false
            });
        }

        combinedSegments.push({
            text: currentEnglishSegment.trim(),
            isEnglish: true,
            isSpace: false,
            isPunctuation: false
        });
    }

    const cleanedSegments = combinedSegments.reduce((acc, curr, idx, arr) => {
        if (curr.isSpace && arr[idx + 1]?.isSpace) {
            return acc;
        }
        acc.push(curr);
        return acc;
    }, []);

    return (
        <View style={{ flexDirection: 'row-reverse', flexWrap: 'wrap', justifyContent: 'flex-start', alignItems: 'flex-start' }}>
            {cleanedSegments.map((segment, index) => (
                <Text
                    key={index}
                    style={{
                        fontFamily: 'Noto',
                        color: '#1D426E',
                        direction: segment.isEnglish ? 'ltr' : 'rtl',
                    }}
                >
                    {segment.text}
                </Text>
            ))}
        </View>
    );
};

// PDF styles (same as ChecklistPDF)
const pdfStyles = StyleSheet.create({
    page: {
        padding: 15,
        fontFamily: 'Noto',
        backgroundColor: 'white',
        color: '#1D426E',
    },
    header: {
        flexDirection: 'row-reverse',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: 10,
        minHeight: 22,
    },
    headerText: {
        textAlign: 'right',
        fontSize: 8,
        color: '#1D426E',
    },
    headerTitleContainer: {
        alignItems: 'center',
    },
    headerTitle: {
        fontSize: 14,
        fontWeight: 'bold',
        color: '#1D426E',
        textAlign: 'center',
    },
    headerLogos: {
        flexDirection: 'row',
        alignItems: 'center',
        height: 22,
    },
    appLogo: {
        maxHeight: 22,
        objectFit: 'contain'
    },
    container: {
        flex: 1,
        direction: 'rtl',
    },
    group: {
        marginBottom: 8,
    },
    groupTitle: {
        fontSize: 11,
        fontWeight: 'bold',
        marginBottom: 4,
        textAlign: 'right',
        color: '#1D426E',
        borderBottomWidth: 1,
        borderBottomColor: '#1D426E',
        borderBottomStyle: 'solid',
        paddingBottom: 2,
    },
    itemsContainer: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        justifyContent: 'space-between',
    },
    itemColumn: {
        width: '48%',
        marginBottom: 3,
    },
    item: {
        flexDirection: 'row-reverse',
        alignItems: 'flex-start',
        marginBottom: 3,
    },
    checkbox: {
        width: 9,
        height: 9,
        borderWidth: 1,
        borderColor: '#1D426E',
        borderStyle: 'solid',
        marginLeft: 4,
        marginTop: 1,
        justifyContent: 'center',
        alignItems: 'center',
        flexShrink: 0,
        backgroundColor: 'white',
    },
    itemContent: {
        flex: 1,
    },
    itemText: {
        fontSize: 8,
        textAlign: 'right',
        color: '#1D426E',
        lineHeight: 1.2,
    },
});

// PDF Document for Template
const TemplateEmptyPDFDocument = ({ template }) => {
    const formatCreationDate = (date) => {
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${day}/${month}/${year}  ${hours}:${minutes}`;
    };

    return (
        <Document>
            <Page size="A4" style={pdfStyles.page}>
                <View style={pdfStyles.header}>
                    <View style={pdfStyles.headerText}>
                        <Text style={{ fontFamily: 'Noto', color: '#1D426E', fontSize: 8 }}>{formatCreationDate(new Date())}</Text>
                    </View>
                    <View style={pdfStyles.headerTitleContainer}>
                        <View style={pdfStyles.headerTitle}>{renderTextWithDirections(template.name || 'צ\'קליסט')}</View>
                    </View>
                    <View style={pdfStyles.headerLogos}>
                        <Image
                            src={appLogo}
                            style={pdfStyles.appLogo}
                        />
                    </View>
                </View>

                <View style={pdfStyles.container}>
                    {template.groups.map((group, groupIdx) => {
                        const items = group.items || [];
                        const midpoint = Math.ceil(items.length / 2);
                        const leftColumn = items.slice(0, midpoint);
                        const rightColumn = items.slice(midpoint);

                        return (
                            <View key={group._id || `group-${groupIdx}`} style={pdfStyles.group}>
                                <View style={pdfStyles.groupTitle}>{renderTextWithDirections(group.title)}</View>

                                <View style={pdfStyles.itemsContainer}>
                                    {/* Right column */}
                                    <View style={pdfStyles.itemColumn}>
                                        {rightColumn.map((item, itemIdx) => (
                                            <View key={item._id || `item-right-${itemIdx}`}>
                                                <View style={pdfStyles.item}>
                                                    <View style={pdfStyles.checkbox} />
                                                    <View style={pdfStyles.itemContent}>
                                                        <View style={pdfStyles.itemText}>{renderTextWithDirections(item.text)}</View>
                                                    </View>
                                                </View>
                                            </View>
                                        ))}
                                    </View>

                                    {/* Left column */}
                                    <View style={pdfStyles.itemColumn}>
                                        {leftColumn.map((item, itemIdx) => (
                                            <View key={item._id || `item-left-${itemIdx}`}>
                                                <View style={pdfStyles.item}>
                                                    <View style={pdfStyles.checkbox} />
                                                    <View style={pdfStyles.itemContent}>
                                                        <View style={pdfStyles.itemText}>{renderTextWithDirections(item.text)}</View>
                                                    </View>
                                                </View>
                                            </View>
                                        ))}
                                    </View>
                                </View>
                            </View>
                        );
                    })}
                </View>
            </Page>
        </Document>
    );
};

// Export Button Component
const ExportTemplateButton = ({ template }) => {
    const [isExporting, setIsExporting] = useState(false);

    const handleExport = async () => {
        if (!template) return;

        try {
            setIsExporting(true);
            const blob = await pdf(<TemplateEmptyPDFDocument template={template} />).toBlob();
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;

            const fileName = `${template.name.replace(/\s+/g, '_')}-${new Date().toISOString().split('T')[0]}.pdf`;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error generating PDF:', error);
            alert('שגיאה ביצירת קובץ PDF');
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <button
            onClick={handleExport}
            disabled={!template || isExporting}
            style={{
                padding: '12px 24px',
                border: '1px solid #103c6e',
                borderRadius: '8px',
                fontSize: '1rem',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
            }}
        >
            {isExporting ? 'מייצא...' : 'ייצוא ל-PDF'}
        </button>
    );
};

function ChecklistTemplateView() {
    const { templateId } = useParams();
    const navigate = useNavigate();
    const [template, setTemplate] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expandedItems, setExpandedItems] = useState({});
    const [creatingInstance, setCreatingInstance] = useState(false);

    useEffect(() => {
        const fetchTemplate = async () => {
            try {
                setLoading(true);
                setError(null);
                const data = await getChecklistTemplateById(templateId);
                setTemplate(data);
            } catch (err) {
                setError('אירעה שגיאה בטעינת התבנית');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        fetchTemplate();
    }, [templateId]);

    const toggleExpand = (itemId) => {
        setExpandedItems(prev => ({ ...prev, [itemId]: !prev[itemId] }));
    };

    const handleCreateInstance = async () => {
        try {
            setCreatingInstance(true);
            const newInstance = await createChecklistInstance(templateId);
            navigate(`/checklists/instance/${newInstance._id}`);
        } catch (err) {
            console.error('Failed to create checklist instance', err);
            setError('אירעה שגיאה ביצירת מופע חדש');
            setCreatingInstance(false);
        }
    };

    if (loading) {
        return (
            <CommonLayout maxWidth="900px">
                <div className="checklist-instance-container loading-container">
                    <Skeleton>
                        <Skeleton.Text />
                        <Skeleton.Block height='200px' />
                        <Skeleton.Block height='200px' />
                    </Skeleton>
                </div>
            </CommonLayout>
        );
    }

    if (error) {
        return (
            <CommonLayout maxWidth="900px">
                <div className="checklist-instance-container">
                    <div className="form-error">{error}</div>
                    <button style={cancelButton} onClick={() => navigate('/checklists')}>
                        חזור לצ'קליסטים
                    </button>
                </div>
            </CommonLayout>
        );
    }

    if (!template) {
        return (
            <CommonLayout maxWidth="900px">
                <div className="checklist-instance-container">
                    <div className="form-error">תבנית לא נמצאה</div>
                    <button style={cancelButton} onClick={() => navigate('/checklists')}>
                        חזור לצ'קליסטים
                    </button>
                </div>
            </CommonLayout>
        );
    }

    return (
        <CommonLayout maxWidth="900px">
            <div className="checklist-instance-container">
                <PageTitle title={template.name} />

                <div className="checklist-groups">
                    {template.groups.map((group, groupIdx) => (
                        <div key={group._id || `group-${groupIdx}`} className="checklist-group">
                            <h2 className="group-title">{group.title}</h2>
                            <div className="checklist-items">
                                {group.items.map((item, itemIdx) => {
                                    const itemKey = item._id || `item-${groupIdx}-${itemIdx}`;
                                    const isExpanded = expandedItems[itemKey];
                                    const hasOptionalFields = item.optionalFields && item.optionalFields.length > 0;

                                    return (
                                        <div key={itemKey} className="checklist-item">
                                            <div className="item-main">
                                                <div className="checkbox-container">
                                                    <input
                                                        type="checkbox"
                                                        id={`checkbox-${itemKey}`}
                                                        className="checklist-checkbox"
                                                        disabled
                                                    />
                                                    <label htmlFor={`checkbox-${itemKey}`} className="checkbox-label">
                                                    </label>
                                                </div>
                                                <span className="item-text">{item.text}</span>

                                                <div className="item-actions">
                                                    {hasOptionalFields && (
                                                        <button
                                                            className="expand-btn"
                                                            onClick={() => toggleExpand(itemKey)}
                                                            title={isExpanded ? 'כווץ' : 'הרחב'}
                                                        >
                                                            {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                                                        </button>
                                                    )}
                                                </div>
                                            </div>

                                            {isExpanded && hasOptionalFields && (
                                                <div className="optional-fields">
                                                    {/* Render details first */}
                                                    {item.optionalFields.filter(f => f.type === 'details').map((field, fieldIndex) => (
                                                        <div key={`details-${fieldIndex}`} className="optional-field">
                                                            <p className="field-details-text">
                                                                {field.value || 'אין פרטים'}
                                                            </p>
                                                        </div>
                                                    ))}

                                                    {/* Render links second */}
                                                    {item.optionalFields.filter(f => f.type === 'link').map((field, fieldIndex) => (
                                                        <div key={`link-${fieldIndex}`} className="optional-field">
                                                            {field.url && (
                                                                <a
                                                                    href={field.url}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="field-link-text"
                                                                >
                                                                    {field.label}
                                                                </a>
                                                            )}
                                                        </div>
                                                    ))}

                                                    {/* Render inputs last - show as read-only placeholders */}
                                                    {item.optionalFields.filter(f => f.type === 'input').map((field, fieldIndex) => (
                                                        <div key={`input-${fieldIndex}`} className="optional-field">
                                                            <label className="field-label">{field.label}</label>
                                                            <div style={{
                                                                padding: '10px',
                                                                backgroundColor: '#f5f5f5',
                                                                borderRadius: '4px',
                                                                color: '#999',
                                                                fontSize: '0.9rem',
                                                                textAlign: 'right'
                                                            }}>
                                                                שדה לתיעוד מידע...
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>

                <div style={pageActionsContainer}>
                    <ExportTemplateButton template={template} />
                    {template.isCloneable && (
                        <button
                            onClick={handleCreateInstance}
                            disabled={creatingInstance}
                            style={{
                                ...confirmButton,
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                            }}
                        >
                            <Copy size={16} />
                            <span>מטופל חדש</span>
                        </button>
                    )}
                    <button
                        style={cancelButton}
                        onClick={() => navigate('/checklists')}
                    >
                        חזור לרשימה
                    </button>
                </div>
            </div>
        </CommonLayout>
    );
}

export default ChecklistTemplateView;
